'use client';

import React, { useState } from 'react';
import { Star, ThumbsUp, ChevronDown } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';

const REVIEWS = [
{
  id: 'rev-001',
  author: 'Priya Sharma',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_155caa031-1763295388947.png",
  avatarAlt: 'Priya Sharma, Indian woman smiling',
  rating: 5,
  date: '14 Apr 2026',
  job: 'Boiler service',
  text: 'James was brilliant from start to finish. Arrived on time, explained everything clearly, and the boiler is running perfectly now. Will definitely use again. Highly recommend.',
  helpful: 12,
  verified: true,
  tier: 'homeowner'
},
{
  id: 'rev-002',
  author: 'David Okonkwo',
  avatar: "https://images.unsplash.com/photo-1621167816523-3d6054da34d9",
  avatarAlt: 'David Okonkwo, Black man in light blue shirt',
  rating: 5,
  date: '2 Apr 2026',
  job: 'Emergency leak repair',
  text: "Called James at 11pm for a burst pipe emergency. He was there within 45 minutes and had it sorted in an hour. Absolute lifesaver. The price was fair given the hour.",
  helpful: 8,
  verified: true,
  tier: 'homeowner'
},
{
  id: 'rev-003',
  author: 'Fatima Al-Hassan',
  avatar: "https://images.unsplash.com/photo-1726661795861-14f2162bea33",
  avatarAlt: 'Fatima Al-Hassan, woman in hijab smiling',
  rating: 5,
  date: '28 Mar 2026',
  job: 'Full bathroom installation',
  text: "James installed our new bathroom suite over 3 days. Meticulous work, no mess left behind, and he even spotted a small issue with our stop cock that could have caused problems later. Excellent.",
  helpful: 15,
  verified: true,
  tier: 'homeowner'
},
{
  id: 'rev-004',
  author: 'Tom Whitfield',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_173a24cfe-1763296471490.png",
  avatarAlt: 'Tom Whitfield, white man in casual clothing',
  rating: 4,
  date: '15 Mar 2026',
  job: 'Gas Safety Certificate',
  text: 'Quick and efficient. Came same day for the gas safety cert for our rental property. Only minor thing — arrived 20 mins late but called ahead to let me know. Good value.',
  helpful: 4,
  verified: true,
  tier: 'homeowner'
},
{
  id: 'rev-005',
  author: 'Sunita Patel',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1334e5402-1770349019845.png",
  avatarAlt: 'Sunita Patel, Indian woman smiling warmly',
  rating: 5,
  date: '5 Mar 2026',
  job: 'Radiator replacement',
  text: "Third time using James. He's become our household's go-to plumber. Always professional, always tidy, and his quotes are always fair. Couldn't recommend more highly.",
  helpful: 9,
  verified: true,
  tier: 'homeowner'
},
{
  id: 'rev-006',
  author: 'Marcus Webb',
  avatar: "https://images.unsplash.com/photo-1636533105123-a47d57a99aa1",
  avatarAlt: 'Marcus Webb, man with beard',
  rating: 4,
  date: '18 Feb 2026',
  job: 'Power flushing',
  text: 'James did a thorough power flush on our central heating. System is much better now — all radiators heating evenly. Took a full day as expected. Good job.',
  helpful: 3,
  verified: true,
  tier: 'homeowner'
},
{
  id: 'rev-007',
  author: 'Claire Hutchinson',
  avatar: "https://img.rocket.new/generatedImages/rocket_gen_img_1278b4b3d-1763298222622.png",
  avatarAlt: 'Claire Hutchinson, woman with glasses',
  rating: 5,
  date: '2 Feb 2026',
  job: 'Underfloor heating install',
  text: "Complex job — new build extension with underfloor heating throughout. James worked alongside our builder seamlessly. Everything passed inspection first time. Fantastic.",
  helpful: 11,
  verified: true,
  tier: 'homeowner'
},
{
  id: 'rev-008',
  author: 'Yusuf Ibrahim',
  avatar: "https://images.unsplash.com/photo-1636533105123-a47d57a99aa1",
  avatarAlt: 'Yusuf Ibrahim, man in casual shirt',
  rating: 5,
  date: '20 Jan 2026',
  job: 'Boiler replacement',
  text: 'New Worcester Bosch boiler installed. James sourced the unit, gave me a fixed price upfront, and completed the install in one day. Heating is transformed. Excellent value.',
  helpful: 7,
  verified: true,
  tier: 'homeowner'
}];


const RATING_BREAKDOWN = [
{ stars: 5, count: 178, key: 'rb-5' },
{ stars: 4, count: 28, key: 'rb-4' },
{ stars: 3, count: 6, key: 'rb-3' },
{ stars: 2, count: 2, key: 'rb-2' },
{ stars: 1, count: 0, key: 'rb-1' }];


const TOTAL_REVIEWS = 214;

type SortOption = 'recent' | 'highest' | 'lowest' | 'helpful';

export default function ReviewsTab() {
  const [sortBy, setSortBy] = useState<SortOption>('recent');
  const [filterRating, setFilterRating] = useState(0);
  const [helpfulVoted, setHelpfulVoted] = useState<Set<string>>(new Set());

  const toggleHelpful = (id: string) => {
    setHelpfulVoted((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else
      next.add(id);
      return next;
    });
  };

  const sorted = [...REVIEWS].
  filter((r) => filterRating === 0 || r.rating === filterRating).
  sort((a, b) => {
    if (sortBy === 'highest') return b.rating - a.rating;
    if (sortBy === 'lowest') return a.rating - b.rating;
    if (sortBy === 'helpful') return b.helpful - a.helpful;
    return 0; // recent — already in order
  });

  return (
    <div className="space-y-5">
      {/* Rating summary */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-card p-6">
        <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
          {/* Big score */}
          <div className="text-center flex-shrink-0">
            <p className="font-display text-5xl font-800 text-gray-900 tabular-nums">4.9</p>
            <div className="flex items-center gap-0.5 justify-center my-1">
              {Array.from({ length: 5 }).map((_, si) =>
              <Star key={`hero-star-${si}`} size={16} className="text-amber-400 fill-amber-400" />
              )}
            </div>
            <p className="text-gray-400 text-sm">{TOTAL_REVIEWS} reviews</p>
          </div>

          {/* Breakdown bars */}
          <div className="flex-1 space-y-1.5 w-full">
            {RATING_BREAKDOWN.map((rb) => {
              const pct = TOTAL_REVIEWS > 0 ? rb.count / TOTAL_REVIEWS * 100 : 0;
              return (
                <button
                  key={rb.key}
                  onClick={() => setFilterRating(filterRating === rb.stars ? 0 : rb.stars)}
                  className={`w-full flex items-center gap-2 group transition-opacity ${filterRating > 0 && filterRating !== rb.stars ? 'opacity-40' : ''}`}>

                  <span className="text-xs text-gray-500 w-4 tabular-nums">{rb.stars}</span>
                  <Star size={11} className="text-amber-400 fill-amber-400 flex-shrink-0" />
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-amber-400 rounded-full transition-all duration-500"
                      style={{ width: `${pct}%` }} />

                  </div>
                  <span className="text-xs text-gray-400 tabular-nums w-6 text-right">{rb.count}</span>
                </button>);

            })}
          </div>
        </div>
        {filterRating > 0 &&
        <button
          onClick={() => setFilterRating(0)}
          className="mt-3 text-xs text-plum-600 hover:text-plum-800 font-medium">

            Clear filter — showing {filterRating}★ only
          </button>
        }
      </div>

      {/* Sort controls */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {sorted.length} review{sorted.length !== 1 ? 's' : ''}
          {filterRating > 0 ? ` · ${filterRating}★ filter active` : ''}
        </p>
        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as SortOption)}
            className="appearance-none pl-3 pr-8 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-plum-400 cursor-pointer text-gray-700">

            <option value="recent">Most recent</option>
            <option value="highest">Highest rated</option>
            <option value="lowest">Lowest rated</option>
            <option value="helpful">Most helpful</option>
          </select>
          <ChevronDown size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
      </div>

      {/* Review cards */}
      <div className="space-y-4">
        {sorted.map((review) =>
        <div key={review.id} className="bg-white rounded-2xl border border-gray-100 shadow-card p-5">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-3">
                <AppImage
                src={review.avatar}
                alt={review.avatarAlt}
                width={40}
                height={40}
                className="w-10 h-10 rounded-full object-cover flex-shrink-0" />

                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-sm text-gray-800">{review.author}</p>
                    {review.verified &&
                  <span className="px-1.5 py-0.5 bg-mint-50 border border-mint-100 rounded text-xs text-mint-700 font-medium">
                        Verified
                      </span>
                  }
                  </div>
                  <p className="text-gray-400 text-xs">{review.job}</p>
                </div>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="flex items-center gap-0.5 justify-end mb-0.5">
                  {Array.from({ length: 5 }).map((_, si) =>
                <Star
                  key={`rev-${review.id}-star-${si}`}
                  size={12}
                  className={si < review.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-200 fill-gray-200'} />

                )}
                </div>
                <p className="text-gray-400 text-xs">{review.date}</p>
              </div>
            </div>
            <p className="text-gray-700 text-sm leading-relaxed mb-3">{review.text}</p>
            <div className="flex items-center gap-2">
              <button
              onClick={() => toggleHelpful(review.id)}
              className={`flex items-center gap-1.5 text-xs font-medium transition-colors ${
              helpfulVoted.has(review.id) ?
              'text-plum-600' : 'text-gray-400 hover:text-gray-600'}`
              }>

                <ThumbsUp size={13} className={helpfulVoted.has(review.id) ? 'fill-plum-600' : ''} />
                Helpful ({review.helpful + (helpfulVoted.has(review.id) ? 1 : 0)})
              </button>
            </div>
          </div>
        )}
      </div>

      {sorted.length === 0 &&
      <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
          <Star size={32} className="text-gray-200 mx-auto mb-3" />
          <p className="font-display font-600 text-gray-600">No reviews match this filter</p>
          <button onClick={() => setFilterRating(0)} className="mt-2 text-sm text-plum-600 hover:text-plum-800">
            Clear filter
          </button>
        </div>
      }
    </div>);

}